import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import { $apiV1 } from "../../config/api";
import { userActions } from "../../store/user/userSlice";
import { getMediaUrl } from "../../helpers";
import { readAuthContextFromStorage } from "../../store/user/userService";
import emptyState from "../../images/contactLogo.svg";

const SEARCH_DEBOUNCE_MS = 250;
const MIN_QUERY_LEN = 2;

// /player/view-as — platform-admin-only screen that lets an admin pick ANY
// player to inspect inside the player PWA, WITHOUT holding the `player` role
// (which would restrict the main app). The chosen id becomes the session's
// "view-as" player: it is mirrored onto mappedPlayerId (so every PWA hook
// works unchanged) and forwarded as `as_player` on /player/* calls — see
// config/api.js and the backend PlayerDataFilter. C1.
const playerDisplayName = (p) =>
  p?.display_name ||
  [p?.name, p?.surname].filter(Boolean).join(" ") ||
  `Player #${p?.id}`;

const playerPhoto = (p) =>
  p?.photos?.[0]?.full
    ? getMediaUrl(p.photos[0].full)
    : (p?.photo ? getMediaUrl(p.photo) : emptyState);

const AdminViewAsPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const userRoles = useSelector((state) => state.user.userRoles);
  const userType = useSelector((state) => state.user.userType);
  const viewAsPlayerId = useSelector((state) => state.user.viewAsPlayerId);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);

  // Only platform admins belong here. Players go to their own dashboard;
  // anyone else back to the main app.
  const isAdmin = useMemo(() => {
    const roles = Array.isArray(userRoles) && userRoles.length > 0
      ? userRoles
      : (userType ? [userType] : readAuthContextFromStorage().roles || []);
    return roles.includes("platform_admin") && !roles.includes("player");
  }, [userRoles, userType]);

  useEffect(() => {
    if (!isAdmin) {
      navigate("/dashboard", { replace: true });
    }
  }, [isAdmin, navigate]);

  useEffect(() => {
    const q = query.trim();
    if (q.length < MIN_QUERY_LEN) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    const handle = setTimeout(async () => {
      setSearching(true);
      setError(null);
      try {
        const res = await $apiV1.get("/platform/player/", {
          params: { page: 1, search: q },
        });
        if (cancelled) return;
        const rows = res.data?.results ?? res.data?.data ?? res.data ?? [];
        setResults(Array.isArray(rows) ? rows : []);
      } catch (e) {
        if (cancelled) return;
        setError("Search failed. Please try again.");
        setResults([]);
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [query]);

  const pick = (player) => {
    const id = player?.id ?? player?.player_id;
    if (!id) return;
    localStorage.setItem("view_as_player_id", String(id));
    localStorage.setItem("mapped_player_id", String(id));
    dispatch(userActions.setViewAsPlayer(Number(id)));
    navigate("/player/dashboard", { replace: true });
  };

  return (
    <Shell>
      <Header>
        <Title>View the player app as…</Title>
        <Subtitle>
          Pick a player to inspect the PWA with their data. This only changes
          what you see in the player app — your admin access is unaffected.
        </Subtitle>
        {viewAsPlayerId ? (
          <Current>Currently viewing player #{viewAsPlayerId}</Current>
        ) : null}
      </Header>

      <SearchInput
        autoFocus
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search players by name…"
      />

      {error ? <ErrorText>{error}</ErrorText> : null}
      {searching ? <Hint>Searching…</Hint> : null}
      {!searching && query.trim().length >= MIN_QUERY_LEN && results.length === 0 && !error ? (
        <Hint>No players found.</Hint>
      ) : null}

      <Results>
        {results.map((p) => (
          <Row key={p.id} type="button" onClick={() => pick(p)}>
            <Avatar src={playerPhoto(p)} alt="" />
            <Name>{playerDisplayName(p)}</Name>
          </Row>
        ))}
      </Results>
    </Shell>
  );
};

export default AdminViewAsPage;

const Shell = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  background: ${(props) => props.theme.black};
  color: ${(props) => props.theme.white};
  padding: 24px 16px 48px;
  max-width: 640px;
  margin: 0 auto;
`;

const Header = styled.div`
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: 700;
  margin: 0 0 8px;
`;

const Subtitle = styled.p`
  font-size: 14px;
  line-height: 1.4;
  opacity: 0.7;
  margin: 0;
`;

const Current = styled.p`
  font-size: 13px;
  margin: 10px 0 0;
  opacity: 0.85;
`;

const SearchInput = styled.input`
  width: 100%;
  box-sizing: border-box;
  padding: 14px 16px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.15);
  background: rgba(255, 255, 255, 0.06);
  color: ${(props) => props.theme.white};
  font-size: 16px;
  outline: none;

  &::placeholder {
    color: rgba(255, 255, 255, 0.4);
  }
`;

const Hint = styled.p`
  font-size: 14px;
  opacity: 0.6;
  margin: 16px 4px;
`;

const ErrorText = styled.p`
  font-size: 14px;
  color: #ff6b6b;
  margin: 16px 4px;
`;

const Results = styled.div`
  margin-top: 12px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Row = styled.button`
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  padding: 10px 12px;
  border: none;
  border-radius: 12px;
  background: transparent;
  color: inherit;
  cursor: pointer;
  text-align: left;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
  }
`;

const Avatar = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  background: rgba(255, 255, 255, 0.1);
  flex-shrink: 0;
`;

const Name = styled.span`
  font-size: 16px;
  font-weight: 500;
`;
